import React from "react";

const futura = "'Futura', sans-serif";

const RAW_FAQ_GROUPS = [
  {
    category: "Ordering & Shipping",
    items: [
      {
        q: "When does the metal Brik ship?",
        a: "Orders are processed in about 1 business day and arrive within 5-7 days.",
      },
      {
        q: "How long does shipping take?",
        a: "USPS transit typically takes 3-4 days after processing, so most orders arrive within 5-7 days of ordering. Depending on USPS, transit can occasionally take up to 7-9 days.",
      },
      {
        q: "Do you offer free shipping?",
        a: "Yes. Shipping is free on every US order, with no minimum.",
      },
      {
        q: "Do you ship internationally?",
        a: "International shipping availability will be displayed at checkout.",
      },
      {
        q: "What's included in the box?",
        a: "The Brik wallet, a rechargeable tracking card, a keyring, two elastic bands, and setup instructions with a tracking card guide.",
      },
      {
        q: "Does The Brik come assembled?",
        a: "Yes. The Brik arrives ready to use right out of the box.",
      },
      {
        q: "What is your return policy?",
        a: "If your order arrives damaged or there is a legitimate issue with the product, contact us and we'll work with you to make it right. Reach out to our support team with your order number and photos of the issue.",
      },
      {
        q: "Does The Brik come with a warranty?",
        a: "Yes. If your Brik experiences a structural failure such as the wallet body snapping during normal use, we will replace it.",
      },
      {
        q: "Is checkout secure?",
        a: "All payments are processed by Stripe (the same payment processor used by Amazon, Shopify, and Google). We never see or store your card details.",
      },
    ],
  },
  {
    category: "Capacity & Everyday Carry",
    items: [
      {
        q: "How many cards can The Brik hold?",
        a: "The Brik can hold 1 ID card in the front slot and approximately 7–8 additional cards in the main compartment.",
      },
      {
        q: "How much cash can The Brik hold?",
        a: "There is no fixed cash limit. The Brik holds a substantial amount of folded cash while maintaining a compact profile. Exact capacity depends on how many cards and keys you're carrying.",
      },
      {
        q: "Will my cards fall out?",
        a: "No. The elastic retention system is designed to securely hold your cards, cash, and essentials during everyday use while still allowing easy access when needed.",
      },
      {
        q: "Does The Brik block RFID signals?",
        a: "Yes. The main card compartment provides RFID protection for your cards. The front ID slot is designed for quick access and is not RFID-blocking.",
      },
      {
        q: "Does The Brik fit in a front pocket?",
        a: "Yes. The Brik was designed to be compact and comfortable for everyday front-pocket carry.",
      },
      {
        q: "Can I travel with The Brik?",
        a: "Yes. The Brik is designed for everyday carry and travel. If you use any optional accessories, please check local transportation and airline regulations.",
      },
    ],
  },
  {
    category: "Tracking Card",
    items: [
      {
        q: "Is the tracking card included?",
        a: "Yes. Every Brik includes a rechargeable tracking card integrated into the design.",
      },
      {
        q: "What can the tracking card do?",
        a: "You can view your wallet's location, ring the wallet from your phone, and quickly locate misplaced items. A setup guide and video instructions are provided with every order.",
      },
      {
        q: "Does the tracking card work with iPhone and Android?",
        a: "Both. Choose Apple or Android at checkout and your Brik ships with the matching tracking card for your phone.",
      },
      {
        q: "How do I charge the tracking card?",
        a: "The tracking card charges wirelessly using a standard wireless charger. A charger is not included.",
      },
      {
        q: "How long does the tracking card battery last?",
        a: "The rechargeable tracking card battery lasts up to 6 months on a single charge, depending on usage.",
      },
      {
        q: "Can I use The Brik without the tracking card?",
        a: "Yes. The tracking card is securely integrated but easily removable, and The Brik functions perfectly as a wallet without it.",
      },
    ],
  },
  {
    category: "Materials & Durability",
    items: [
      {
        q: "What material is The Brik made from?",
        a: "The Brik is made from anodized aluminum for a lightweight and durable finish.",
      },
      {
        q: "How is it different from the original plastic Brik?",
        a: "Same minimalist layout: ID in front, cards in the middle, cash in the back, integrated key holder, in-built tracking card. The metal version is machined from solid aluminum and is built to last.",
      },
      {
        q: "What colors are available?",
        a: "The current metal version is available in black anodized aluminum. Additional colors may be released in future production runs.",
      },
      {
        q: "Will the aluminum scratch?",
        a: "Like any metal everyday-carry product, minor cosmetic wear can occur over time. The anodized finish is designed to provide durability during everyday use.",
      },
      {
        q: "How much does The Brik weigh?",
        a: "The Brik weighs 1.2 oz (34 grams) and measures 8.5 x 5.5 x 1.5 cm, about the footprint of a credit card. Light enough for front-pocket carry with the durability of aluminum.",
      },
      {
        q: "Is The Brik waterproof?",
        a: "The aluminum body is water-resistant, but the tracking card and other components should not be submerged in water.",
      },
    ],
  },
  {
    category: "Keyring & Elastic Bands",
    items: [
      {
        q: "Is the keyring included?",
        a: "Yes. Every Brik comes with a keyring included.",
      },
      {
        q: "Can I replace or customize the keyring?",
        a: "Yes. The keyring is fully removable and can be replaced with a keyring of your choice.",
      },
      {
        q: "How many elastic bands are included?",
        a: "Every Brik includes two elastic bands.",
      },
      {
        q: "What if my elastic band wears out?",
        a: "Replacement bands will be available, and every order includes a spare elastic band.",
      },
    ],
  },
];

export const FAQ_GROUPS = RAW_FAQ_GROUPS;

export const FAQ_ITEMS = FAQ_GROUPS.flatMap((g) => g.items);

const FAQSection = ({ background = "#f7f7f7" }) => {
  const isLight = background === "white" || background === "#fff" || background === "#ffffff";
  const groupBg = isLight ? "#f7f7f7" : "white";
  const itemBg = isLight ? "white" : "#f7f7f7";

  return (
    <section
      style={{
        background,
        padding: "70px 24px",
      }}
    >
      <style>{`
        .faq-group > summary { list-style: none; }
        .faq-group > summary::-webkit-details-marker { display: none; }
        .faq-group .faq-group-chevron { transition: transform 0.25s ease; }
        .faq-group[open] > summary .faq-group-chevron { transform: rotate(180deg); }
        .faq-item > summary { list-style: none; }
        .faq-item > summary::-webkit-details-marker { display: none; }
      `}</style>
      <div style={{ maxWidth: "780px", margin: "0 auto" }}>
        <h2
          style={{
            fontFamily: futura,
            fontSize: "clamp(28px, 3vw, 38px)",
            margin: "0 0 8px",
            letterSpacing: "-0.5px",
            textAlign: "center",
            color: "#111",
          }}
        >
          Frequently asked questions
        </h2>
        <p
          style={{
            fontFamily: futura,
            color: "#666",
            fontSize: "15px",
            textAlign: "center",
            margin: "0 0 40px",
          }}
        >
          Everything you need to know before you order.
        </p>
        {FAQ_GROUPS.map((group) => (
          <details
            key={group.category}
            className="faq-group"
            style={{
              background: groupBg,
              borderRadius: "14px",
              border: "1px solid #e6e6e6",
              padding: "20px 24px",
              marginBottom: "14px",
              fontFamily: futura,
            }}
          >
            <summary
              style={{
                cursor: "pointer",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: "12px",
              }}
            >
              <span>
                <span
                  style={{
                    display: "block",
                    color: "#ff7d1a",
                    fontSize: "11px",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "1.5px",
                    marginBottom: "2px",
                  }}
                >
                  {group.items.length} questions
                </span>
                <span
                  style={{
                    color: "#111",
                    fontSize: "18px",
                    fontWeight: 700,
                    letterSpacing: "-0.3px",
                  }}
                >
                  {group.category}
                </span>
              </span>
              <span
                className="faq-group-chevron"
                aria-hidden="true"
                style={{
                  color: "#ff7d1a",
                  fontSize: "18px",
                  flexShrink: 0,
                  lineHeight: 1,
                }}
              >
                ▾
              </span>
            </summary>
            <div style={{ marginTop: "16px" }}>
              {group.items.map((item) => (
                <details
                  key={item.q}
                  className="faq-item"
                  style={{
                    background: itemBg,
                    borderRadius: "10px",
                    padding: "16px 20px",
                    marginBottom: "10px",
                    border: "1px solid #e6e6e6",
                    fontFamily: futura,
                    color: "#222",
                  }}
                >
                  <summary
                    style={{
                      cursor: "pointer",
                      fontWeight: 700,
                      fontSize: "15px",
                      listStyle: "none",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: "12px",
                    }}
                  >
                    {item.q}
                    <span
                      style={{ color: "#ff7d1a", fontSize: "20px", flexShrink: 0 }}
                      aria-hidden="true"
                    >
                      +
                    </span>
                  </summary>
                  <p
                    style={{
                      margin: "10px 0 0",
                      fontSize: "15px",
                      lineHeight: 1.55,
                      color: "#555",
                    }}
                  >
                    {item.a}
                  </p>
                </details>
              ))}
            </div>
          </details>
        ))}
        <p
          style={{
            fontFamily: futura,
            color: "#666",
            fontSize: "14px",
            textAlign: "center",
            margin: "8px 0 0",
          }}
        >
          Still have a question? Email{" "}
          <a
            href="mailto:asray@the-brik.com"
            style={{ color: "#ff7d1a", fontWeight: 600 }}
          >
            asray@the-brik.com
          </a>
          .
        </p>
      </div>
    </section>
  );
};

export default FAQSection;
